// MaterialPage — sistema de material.
//
// Duas caras na mesma página, conforme o papel de quem abre:
//   • Gerência (2/3): monta o roteiro do dia — escolhe a data e vai clicando
//     nos hotéis na ordem em que a funcionária vai dirigir.
//   • Funcionária do material (5), supervisão e gerência: percorre a trilha.
//     Cada hotel é uma parada; ao abrir mostra os recados escritos na semana
//     pelas meninas do POS e o checklist obrigatório. Fechando o hotel, o
//     próximo acende.
//
// O histórico de recados vem de material_notes, que acumula em vez de
// sobrescrever (ver material.py / supplies.py).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogContent, TextField,
  CircularProgress, Alert, Chip, Snackbar, Tooltip, LinearProgress, Collapse,
  MenuItem,
} from '@mui/material';
import {
  MapPin, Check, Lock, ChevronLeft, ChevronRight, Route as RouteIcon,
  MessageSquareText, ClipboardList, PenLine, Trash2, Plus, Flag, X, Car,
  Eye, Search, Inbox, PackageCheck, ChevronDown, ChevronUp, History, Navigation,
  // `Map` daria conflito com o Map nativo, que este arquivo usa para agrupar.
  Map as MapIcon, LocateFixed, ShieldAlert, RefreshCw, MapPinned,
} from 'lucide-react';
import gsap from 'gsap';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SignatureSvg from './SignatureSvg';

// ── Paleta: estrada / viagem ────────────────────────────────────────────────
const C = {
  ink:        '#12203a',
  sub:        '#5b6b86',
  line:       '#dfe6f0',
  road:       '#c9d4e5',
  done:       '#12a594',
  doneDeep:   '#0b7d70',
  active:     '#f0a202',
  activeDeep: '#c07f00',
  locked:     '#aeb9ca',
  bg:         '#f5f8fc',
  card:       '#ffffff',
};

const SECTION_ICON = {
  kiosk:  '🏝️',
  drawer: '🗄️',
  under:  '📦',
  folder: '📂',
  people: '👭',
  // seções que só existem no checklist Sand Art
  treasures: '💎',
  sand:      '🏖️',
  baskets:   '🧺',
};

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const todayISO = () => toISO(new Date());
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const prettyDate = (s) =>
  fromISO(s).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const shortTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// ═══════════════════════════════════════════════════════════════════════════
// Trilha — o mapa do jogo
// ═══════════════════════════════════════════════════════════════════════════
const TrailStop = ({ stop, index, total, unlocked, onOpen }) => {
  const pulseRef = useRef(null);

  const isDone = stop.status === 'DONE';
  const isActive = !isDone && unlocked;

  // O nó ativo respira, para o olho achar sozinho onde ela parou.
  useLayoutEffect(() => {
    if (!isActive || !pulseRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.to(pulseRef.current, {
        scale: 1.55, opacity: 0, duration: 1.6, ease: 'power2.out',
        repeat: -1, repeatDelay: 0.15,
      });
    }, pulseRef);
    return () => ctx.revert();
  }, [isActive]);

  const side = index % 2 === 0 ? 'row' : 'row-reverse';
  const isLocked = !isDone && !isActive;
  // Toda parada abre. A travada abre só para leitura, para ela poder espiar
  // o que vem pela frente e já separar o material antes de sair dirigindo.
  const clickable = true;

  return (
    <Box
      data-trail-node
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, width: '100%',
        flexDirection: side, mb: 1,
      }}
    >
      {/* Nó */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        {isActive && (
          <Box ref={pulseRef} sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: `3px solid ${C.active}`, pointerEvents: 'none',
          }} />
        )}
        <Box
          onClick={() => onOpen(stop)}
          role="button"
          aria-label={`${stop.hotel_name} — stop ${index + 1} of ${total}${isLocked ? ' (preview)' : ''}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(stop); }
          }}
          sx={{
            width: 66, height: 66, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
            cursor: 'pointer',
            background: isDone
              ? `linear-gradient(145deg, ${C.done}, ${C.doneDeep})`
              : isActive
                ? `linear-gradient(145deg, ${C.active}, ${C.activeDeep})`
                : '#e8edf5',
            color: clickable ? '#fff' : C.locked,
            boxShadow: isActive
              ? '0 8px 22px rgba(240,162,2,0.45)'
              : isDone ? '0 4px 14px rgba(18,165,148,0.30)' : 'none',
            border: clickable ? 'none' : `2px dashed ${C.road}`,
            transition: 'transform .18s',
            '&:hover': { transform: clickable ? 'scale(1.06)' : 'none' },
            '&:focus-visible': { outline: `3px solid ${C.ink}`, outlineOffset: 3 },
          }}
        >
          {isDone ? <Check size={26} strokeWidth={3} />
            : isActive ? <MapPin size={24} strokeWidth={2.5} />
              : <Lock size={20} />}
          <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: '.06em', mt: .2 }}>
            {index + 1}/{total}
          </Typography>
        </Box>
      </Box>

      {/* Cartão do hotel */}
      <Box
        onClick={() => onOpen(stop)}
        sx={{
          flex: 1, minWidth: 0, px: 2, py: 1.5, borderRadius: 3,
          bgcolor: C.card,
          border: `1.5px solid ${isActive ? C.active : C.line}`,
          boxShadow: isActive ? '0 6px 18px rgba(240,162,2,0.16)' : '0 1px 4px rgba(0,0,0,0.05)',
          opacity: isLocked ? 0.72 : 1,
          cursor: 'pointer',
          textAlign: index % 2 === 0 ? 'left' : 'right',
        }}
      >
        <Typography sx={{ fontWeight: 800, color: C.ink, lineHeight: 1.2 }} noWrap>
          {stop.hotel_name}
        </Typography>
        <Box sx={{
          display: 'flex', gap: 1, alignItems: 'center', mt: .5, flexWrap: 'wrap',
          justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
        }}>
          {isDone ? (
            <Chip size="small" label={`Done · ${shortTime(stop.completed_at)}`}
              sx={{ bgcolor: 'rgba(18,165,148,.12)', color: C.doneDeep, fontWeight: 700, height: 22 }} />
          ) : isActive ? (
            <Chip size="small" label="Your stop now"
              sx={{ bgcolor: 'rgba(240,162,2,.16)', color: C.activeDeep, fontWeight: 800, height: 22 }} />
          ) : (
            <Chip size="small" icon={<Eye size={13} />} label="Tap to preview"
              sx={{ bgcolor: '#eef2f8', color: C.sub, fontWeight: 700, height: 22 }} />
          )}
          {stop.material_notes?.length > 0 && (
            <Chip size="small" icon={<MessageSquareText size={13} />}
              label={stop.material_notes.length}
              sx={{ bgcolor: '#eef4ff', color: '#3557b7', fontWeight: 700, height: 22 }} />
          )}
          {stop.checked_count > 0 && !isDone && (
            <Chip size="small" label={`${stop.checked_count}/${stop.total_items}`}
              sx={{ bgcolor: '#f2f5fa', color: C.sub, fontWeight: 700, height: 22 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Trail = ({ stops, onOpen }) => {
  const wrapRef = useRef(null);
  const roadRef = useRef(null);

  // Só a primeira parada não concluída fica liberada — é o que dá a sensação
  // de trilha guiada em vez de lista.
  const firstUnlockedId = useMemo(() => {
    const next = stops.find((s) => s.status !== 'DONE');
    return next ? next.id : null;
  }, [stops]);

  useLayoutEffect(() => {
    if (!wrapRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-trail-node]', {
        y: 26, opacity: 0, duration: 0.5, stagger: 0.09, ease: 'back.out(1.4)',
      });
      if (roadRef.current) {
        gsap.fromTo(roadRef.current,
          { scaleY: 0, transformOrigin: 'top center' },
          { scaleY: 1, duration: 0.9, ease: 'power2.out' });
      }
    }, wrapRef);
    return () => ctx.revert();
  }, [stops.length]);

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', mt: 2 }}>
      {/* Estrada tracejada de fundo */}
      <Box ref={roadRef} sx={{
        position: 'absolute', left: 33, top: 20, bottom: 20, width: 6, borderRadius: 3,
        background: `repeating-linear-gradient(to bottom, ${C.road} 0 14px, transparent 14px 26px)`,
        zIndex: 0,
      }} />
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column' }}>
        {stops.map((s, i) => (
          <TrailStop
            key={s.id}
            stop={s}
            index={i}
            total={stops.length}
            unlocked={s.id === firstUnlockedId}
            onOpen={onOpen}
          />
        ))}
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Assinatura desenhada
//
// Mesmo formato usado nos checklists do POS: um JSON com a lista de paths SVG
// num viewBox 520x180, que o SignatureSvg.jsx já sabe renderizar. Assim a
// assinatura do material sai igual à do opening/closing.
// ═══════════════════════════════════════════════════════════════════════════
const SIG_W = 520;
const SIG_H = 180;

const SignaturePad = ({ value, onChange, disabled }) => {
  const svgRef = useRef(null);
  const [paths, setPaths] = useState([]);
  const [current, setCurrent] = useState('');
  const drawing = useRef(false);
  // Enquanto o dedo desenha, a modal não pode rolar junto. touch-action sozinho
  // não bastava: o container da modal continuava puxando a tela pra cima e pra
  // baixo no meio da assinatura.
  const lockedScroller = useRef(null);

  const lockScroll = () => {
    const sc = svgRef.current?.closest('.MuiDialogContent-root');
    if (!sc) return;
    lockedScroller.current = { el: sc, prev: sc.style.overflowY };
    sc.style.overflowY = 'hidden';
  };

  const unlockScroll = () => {
    const l = lockedScroller.current;
    if (!l) return;
    l.el.style.overflowY = l.prev || '';
    lockedScroller.current = null;
  };

  // Se o componente sair no meio de um traço, não deixa a modal travada.
  useEffect(() => unlockScroll, []);

  // Reidrata quando reabre uma parada já assinada.
  useEffect(() => {
    if (!value) { setPaths([]); setCurrent(''); return; }
    try { setPaths(JSON.parse(value) || []); } catch { setPaths([]); }
  }, [value]);

  // Converte o ponto do dedo/mouse para as coordenadas do viewBox, senão o
  // traço sai deslocado em telas de tamanhos diferentes.
  const pointAt = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * SIG_W,
      y: ((e.clientY - r.top) / r.height) * SIG_H,
    };
  };

  const commit = (list) => onChange(list.length ? JSON.stringify(list) : '');

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    lockScroll();
    const p = pointAt(e);
    setCurrent(`M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const p = pointAt(e);
    setCurrent((d) => `${d} L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    unlockScroll();
    setCurrent((d) => {
      if (!d) return '';
      const next = [...paths, d];
      setPaths(next);
      commit(next);
      return '';
    });
  };

  const clear = () => {
    setPaths([]);
    setCurrent('');
    commit([]);
  };

  const empty = paths.length === 0 && !current;

  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <Box
          component="svg"
          ref={svgRef}
          viewBox={`0 0 ${SIG_W} ${SIG_H}`}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          sx={{
            width: '100%', height: 160, display: 'block', borderRadius: 2,
            bgcolor: '#fbfcfe',
            border: `2px ${empty ? 'dashed' : 'solid'} ${empty ? C.road : C.done}`,
            touchAction: 'none',           // sem isso o dedo rola a página em vez de desenhar
            cursor: disabled ? 'default' : 'crosshair',
          }}
        >
          {/* Linha de assinatura, como no papel */}
          <line x1="40" y1={SIG_H - 42} x2={SIG_W - 40} y2={SIG_H - 42}
            stroke={C.road} strokeWidth="2" strokeDasharray="6 6" />
          {paths.map((d, i) => (
            <path key={i} d={d} stroke="#111827" strokeWidth="2.5" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {current && (
            <path d={current} stroke="#111827" strokeWidth="2.5" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
          )}
        </Box>

        {empty && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
            pointerEvents: 'none', color: C.locked,
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <PenLine size={22} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, mt: .5 }}>
                Sign here with your finger
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {!disabled && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: .75 }}>
          <Button size="small" onClick={clear} disabled={empty}
            sx={{ textTransform: 'none', fontWeight: 700, color: C.sub }}>
            Clear
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Endereço -> link de mapa.
//
// Tem que ser um <a href> de verdade, e sem target="_blank". Dois motivos, os
// dois já vistos num iPhone:
//   1. window.open deixava uma aba em branco para trás quando o app assumia.
//   2. Universal Link do iOS só dispara em toque num link — de window.open ou
//      location.href ele não dispara, e aí o endereço abria no navegador antes
//      de chegar no app.
// Com o link direto, o iPhone com Google Maps instalado abre o app na hora, o
// Android idem, e no desktop cai no site do Maps, que é o certo lá.
const mapsHref = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

// ═══════════════════════════════════════════════════════════════════════════
// Onde ela está
//
// Tudo nativo do browser: nada de chave, nada de servidor. A posição é lida no
// celular dela e comparada com os pins aqui mesmo — não sobe pro backend nem
// fica gravada. Serve para o fluxo dela, não como comprovante: localização de
// browser é falsificável, quem vale como registro é a assinatura.
// ═══════════════════════════════════════════════════════════════════════════

// GPS de celular erra 50-100m com facilidade, e o quiosque quase nunca está no
// ponto exato do endereço do resort. Raio apertado só geraria "você não chegou"
// com ela parada na frente do hotel.
const ARRIVED_M = 250;

// Haversine — distância em metros entre dois pontos.
const distanceM = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371000;
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const prettyDistance = (m) => {
  if (m == null) return '';
  if (m < 950) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(m < 9500 ? 1 : 0)} km`;
};

// O detalhe que decide se isto funciona no dia a dia: depois que ela toca em
// "Block", o browser não deixa mais NENHUM site pedir de novo — a chamada falha
// na hora, sem prompt, e não existe API para reabrir o pedido. Só ela consegue,
// nas permissões do site. Por isso o estado 'denied' mostra o passo a passo, e
// o `change` da Permissions API pega o instante em que ela libera, sem precisar
// recarregar a página nem apertar nada.
const useMyLocation = () => {
  const [position, setPosition] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  // idle | locating | ok | denied | error | unsupported
  const [status, setStatus] = useState('idle');

  const locate = useCallback(() => {
    if (!navigator.geolocation) { setStatus('unsupported'); return; }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPosition({ lat: p.coords.latitude, lng: p.coords.longitude });
        setAccuracy(p.coords.accuracy);
        setStatus('ok');
      },
      (err) => setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error'),
      // Sem watchPosition: ficar ouvindo o GPS o dia inteiro come a bateria dela.
      // maximumAge reaproveita uma posição recente em vez de acordar o chip à toa.
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  }, []);

  useEffect(() => {
    // Safari antigo não tem Permissions API — aí fica no botão manual mesmo.
    if (!navigator.permissions?.query) return undefined;
    let perm = null;
    let dead = false;   // a query é assíncrona e pode voltar depois do unmount
    const sync = () => {
      if (!perm || dead) return;
      if (perm.state === 'granted') locate();          // já autorizou: nem pergunta
      else if (perm.state === 'denied') setStatus('denied');
    };
    navigator.permissions.query({ name: 'geolocation' })
      .then((p) => {
        if (dead) return;
        perm = p;
        sync();
        perm.addEventListener('change', sync);
      })
      .catch(() => {});
    return () => { dead = true; perm?.removeEventListener('change', sync); };
  }, [locate]);

  return { position, accuracy, status, locate };
};

const LocationBanner = ({ status, accuracy, onLocate, compact }) => {
  if (status === 'ok') {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: .75, px: 1.5, py: .75,
        borderRadius: 2.5, bgcolor: 'rgba(18,165,148,.09)',
        border: `1px solid rgba(18,165,148,.35)`, mb: compact ? 0 : 1.5,
      }}>
        <LocateFixed size={15} color={C.doneDeep} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.doneDeep }}>
          Location on{accuracy ? ` · ±${Math.round(accuracy)} m` : ''}
        </Typography>
        <IconButton size="small" onClick={onLocate} sx={{ ml: 'auto', color: C.doneDeep }}
          aria-label="Refresh my location">
          <RefreshCw size={14} />
        </IconButton>
      </Box>
    );
  }

  if (status === 'denied') {
    return (
      <Alert
        severity="warning" icon={<ShieldAlert size={18} />}
        sx={{ borderRadius: 3, mb: compact ? 0 : 1.5 }}
        action={
          <Button size="small" onClick={onLocate}
            sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}>
            Try again
          </Button>
        }
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, mb: .5 }}>
          Location is blocked for this site
        </Typography>
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.5 }}>
          The browser will not ask again — you have to turn it back on yourself:
          <br />• <b>iPhone (Safari)</b>: tap <b>aA</b> or <b>ⓘ</b> on the left of the
          address bar → Website Settings → Location → <b>Allow</b>.
          <br />• <b>Android (Chrome)</b>: tap the <b>lock</b> icon next to the address
          → Permissions → Location → <b>Allow</b>.
          <br />It turns on here by itself as soon as you allow it.
        </Typography>
      </Alert>
    );
  }

  if (status === 'error' || status === 'unsupported') {
    return (
      <Alert severity="info" sx={{ borderRadius: 3, mb: compact ? 0 : 1.5 }}
        action={status === 'error' ? (
          <Button size="small" onClick={onLocate} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Try again
          </Button>
        ) : null}>
        {status === 'unsupported'
          ? 'This browser cannot share your location — the map still works, it just will not show where you are.'
          : 'Could not get your location right now. It happens indoors or with a weak signal.'}
      </Alert>
    );
  }

  return (
    <Button
      onClick={onLocate}
      disabled={status === 'locating'}
      startIcon={status === 'locating'
        ? <CircularProgress size={15} color="inherit" />
        : <LocateFixed size={16} />}
      sx={{
        textTransform: 'none', fontWeight: 800, borderRadius: 2.5, px: 2, py: .9,
        color: C.ink, bgcolor: '#fff', border: `1.5px solid ${C.line}`,
        mb: compact ? 0 : 1.5,
        '&:hover': { borderColor: C.active, bgcolor: 'rgba(240,162,2,.06)' },
      }}
    >
      {status === 'locating' ? 'Finding you…' : 'Use my location'}
    </Button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Barra de navegação — ação principal enquanto ela está no carro
//
// Fica colada embaixo da tela e aponta sempre para a próxima parada não
// concluída. Quando ela fecha um hotel, o roteiro recarrega e o botão já troca
// para o endereço seguinte sozinho.
// ═══════════════════════════════════════════════════════════════════════════
const NavigateBar = ({ stop, total, doneCount, distance, onOpen }) => {
  const barRef = useRef(null);
  const hasAddress = !!stop?.hotel_address;
  const arrived = distance != null && distance <= ARRIVED_M;

  // Troca de hotel dá um pulinho, para ela perceber que o destino mudou.
  useLayoutEffect(() => {
    if (!barRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(barRef.current,
        { y: 14, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, ease: 'back.out(1.6)' });
    }, barRef);
    return () => ctx.revert();
  }, [stop?.id]);

  if (!stop) return null;

  return (
    <Box ref={barRef} sx={{
      position: 'sticky', bottom: 12, zIndex: 10, mt: 2,
      // respira longe do fim da lista para não colar no último cartão
      mx: { xs: 0, sm: 0 },
    }}>
      <Box sx={{
        borderRadius: 4, overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(18,32,58,.30)',
        border: `1.5px solid ${arrived ? C.done : C.line}`, bgcolor: '#fff',
      }}>
        <Box sx={{
          px: 2, pt: 1.25, pb: 1,
          display: 'flex', alignItems: 'baseline', gap: 1,
        }}>
          <Typography sx={{
            fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase',
            fontWeight: 800, color: arrived ? C.doneDeep : C.sub,
          }}>
            {arrived ? 'You arrived' : 'Next stop'} · {doneCount + 1} of {total}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 1.25 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 19, color: C.ink, lineHeight: 1.15 }} noWrap>
            {stop.hotel_name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: C.sub }} noWrap>
            {distance != null && !arrived ? `${prettyDistance(distance)} away · ` : ''}
            {hasAddress ? stop.hotel_address : 'No address saved for this resort yet'}
          </Typography>
        </Box>

        {/* Chegou: a ação deixa de ser dirigir e passa a ser abrir o checklist. */}
        {arrived ? (
          <Box
            component="button"
            type="button"
            onClick={() => onOpen?.(stop)}
            sx={{
              width: '100%', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25,
              py: 1.75, color: '#fff', fontFamily: 'inherit',
              background: `linear-gradient(135deg, ${C.done}, ${C.doneDeep})`,
              fontWeight: 900, fontSize: 17,
              '&:active': { filter: 'brightness(.94)' },
              '&:focus-visible': { outline: `3px solid ${C.active}`, outlineOffset: -3 },
            }}
          >
            <MapPinned size={20} />
            You are here — open the checklist
          </Box>
        ) : hasAddress ? (
          <Box
            component="a"
            href={mapsHref(stop.hotel_address)}
            rel="noopener"
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25,
              py: 1.75, textDecoration: 'none', color: '#fff',
              background: `linear-gradient(135deg, #3f6fd8, #2b478f)`,
              fontWeight: 900, fontSize: 17,
              '&:hover': { background: 'linear-gradient(135deg, #3560c4, #223a78)' },
              '&:active': { filter: 'brightness(.94)' },
              '&:focus-visible': { outline: `3px solid ${C.active}`, outlineOffset: -3 },
            }}
          >
            <Navigation size={20} />
            Navigate to {stop.hotel_name}
          </Box>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: 1.6, bgcolor: '#f2f5fa', color: C.sub, fontWeight: 800, fontSize: 14,
          }}>
            <Navigation size={17} />
            Add the address in Setup &gt; Resorts
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Bloco dobrável. Tudo no painel da parada usa isto: com as seções abertas o
// modal passava de 3600px de altura e a assinatura ficava soterrada lá no fim,
// que era o motivo de a tela fugir enquanto ela tentava assinar.
const Section = ({ icon, title, badge, badgeTone, defaultOpen = false, dense, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box data-panel-block sx={{
      mb: 1, bgcolor: '#fff', borderRadius: 3, border: `1px solid ${C.line}`, overflow: 'hidden',
    }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        sx={{
          px: 1.5, py: dense ? .9 : 1.15, display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', bgcolor: open ? '#f7f9fd' : 'transparent',
          '&:hover': { bgcolor: '#f7f9fd' },
          '&:focus-visible': { outline: `2px solid ${C.active}`, outlineOffset: -2 },
        }}
      >
        {icon}
        <Typography sx={{ fontWeight: 800, color: C.ink, fontSize: dense ? 13.5 : 14.5, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {badge != null && (
          <Chip size="small" label={badge} sx={{
            ml: 'auto', height: 20, fontWeight: 800, fontSize: 11,
            bgcolor: badgeTone === 'done' ? 'rgba(18,165,148,.15)'
              : badgeTone === 'warn' ? 'rgba(240,162,2,.16)' : '#eef4ff',
            color: badgeTone === 'done' ? C.doneDeep
              : badgeTone === 'warn' ? C.activeDeep : '#3557b7',
          }} />
        )}
        <Box sx={{ ml: badge != null ? 0 : 'auto', display: 'flex', color: C.sub }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${C.line}` }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Painel de uma parada — recados + checklist + assinatura
// ═══════════════════════════════════════════════════════════════════════════
const StopPanel = ({ open, stop, template, onClose, onToggle, onComplete, busy, preview, supply }) => {
  const [signature, setSignature] = useState('');
  const [signatureSvg, setSignatureSvg] = useState('');
  const [notes, setNotes] = useState('');
  const [localChecks, setLocalChecks] = useState({});
  const ringRef = useRef(null);
  const bodyRef = useRef(null);

  const stopId = stop?.id;

  useEffect(() => {
    if (!stop) return;
    setLocalChecks(stop.checks || {});
    setSignature(stop.signature || '');
    setSignatureSvg(stop.signature_svg || '');
    setNotes(stop.notes || '');
    // Só quando troca de parada — não a cada re-render do objeto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopId]);

  const total = template?.total_items || 0;
  const checkedCount = useMemo(
    () => Object.values(localChecks).filter(Boolean).length,
    [localChecks],
  );
  const pct = total > 0 ? Math.round((checkedCount / total) * 100) : 0;
  const isDone = stop?.status === 'DONE';
  const allChecked = total > 0 && checkedCount >= total;
  // Trancado (ainda não é a vez dela) ou já fechado: só leitura.
  const locked = !!preview || isDone;

  // O anel persegue o valor em vez de pular.
  useLayoutEffect(() => {
    if (!ringRef.current) return;
    gsap.to(ringRef.current, { '--pct': pct, duration: 0.5, ease: 'power2.out' });
  }, [pct]);

  useLayoutEffect(() => {
    if (!open || !bodyRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-panel-block]', {
        y: 18, opacity: 0, duration: 0.42, stagger: 0.07, ease: 'power3.out',
      });
    }, bodyRef);
    return () => ctx.revert();
  }, [open, stopId]);

  const toggle = (key) => {
    if (locked) return;
    const next = !localChecks[key];
    setLocalChecks((p) => ({ ...p, [key]: next }));
    onToggle(stop.id, key, next);
  };

  if (!stop) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      {/* Cabeçalho */}
      <Box sx={{
        px: 2.5, py: 2, color: '#fff',
        background: isDone
          ? `linear-gradient(135deg, ${C.done}, ${C.doneDeep})`
          : `linear-gradient(135deg, #2b3f66, ${C.ink})`,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, letterSpacing: '.14em', opacity: .85, fontWeight: 700 }}>
            STOP {stop.position}{stop.is_sandart ? ' · SAND ART' : ''}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
            {stop.hotel_name}
          </Typography>
        </Box>

        <Box ref={ringRef} sx={{
          '--pct': 0,
          width: 58, height: 58, borderRadius: '50%', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: 'conic-gradient(#fff calc(var(--pct) * 1%), rgba(255,255,255,.22) 0)',
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '50%',
            bgcolor: isDone ? C.doneDeep : C.ink,
            display: 'grid', placeItems: 'center',
          }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{pct}%</Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close">
          <X size={20} />
        </IconButton>
      </Box>

      {/* Endereço + abrir no maps do celular */}
      {stop.hotel_address && (
        <Box
          component="a"
          href={mapsHref(stop.hotel_address)}
          rel="noopener"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.25,
            bgcolor: '#eef4ff', borderBottom: `1px solid ${C.line}`, cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': { bgcolor: '#e3ecff' },
            '&:focus-visible': { outline: `2px solid ${C.active}`, outlineOffset: -2 },
          }}
        >
          <Navigation size={18} color="#3557b7" style={{ flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#3557b7' }}>
              Open in maps
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.sub }} noWrap>
              {stop.hotel_address}
            </Typography>
          </Box>
          <ChevronRight size={17} color="#3557b7" />
        </Box>
      )}

      <DialogContent ref={bodyRef} sx={{ bgcolor: C.bg, p: 2 }}>
        {preview && !isDone && (
          <Alert data-panel-block severity="info" icon={<Eye size={18} />}
            sx={{ borderRadius: 2, mb: 2 }}>
            Preview — this hotel is not your turn yet. Read what they need and get the
            material ready; you can tick the list when you get there.
          </Alert>
        )}

        {/* ── Recados da semana ── */}
        <Section
          icon={<MessageSquareText size={16} color="#3557b7" />}
          title="What they wrote this week"
          badge={stop.material_notes?.length || 0}
          defaultOpen={!!stop.material_notes?.length}
        >
          {stop.material_notes?.length ? (
            stop.material_notes.map((n, i) => (
              <Box key={`${n.created_at}-${i}`} sx={{
                px: 1.5, py: 1.1, borderTop: i === 0 ? 'none' : `1px solid ${C.line}`,
                borderLeft: '4px solid #3557b7',
              }}>
                <Typography sx={{ whiteSpace: 'pre-wrap', color: C.ink, fontSize: 14 }}>
                  {n.note}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: C.sub, mt: .3 }}>
                  {shortTime(n.created_at)}{n.author_name ? ` · ${n.author_name}` : ''}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13.5, color: C.sub }}>
              Nothing written for this hotel this week.
            </Typography>
          )}
        </Section>

        {/* ── Últimas quantidades contadas no POS ── */}
        {supply && (
          <Section
            icon={<PackageCheck size={16} color={C.ink} />}
            title="Last count here"
            badge={(() => {
              const a = daysSince(supply.entry_date);
              return a === 0 ? 'today' : a === 1 ? 'yesterday' : a === null ? '—' : `${a}d ago`;
            })()}
            badgeTone={daysSince(supply.entry_date) >= 3 ? 'warn' : 'info'}
          >
            <Box sx={{
              p: 1, display: 'grid', gap: .6,
              gridTemplateColumns: 'repeat(auto-fit, minmax(74px, 1fr))',
            }}>
              {SUPPLY_FIELDS.map((f) => {
                const v = Number(supply[f.key]) || 0;
                const low = f.target && v < f.target;
                return (
                  <Box key={f.key} sx={{
                    px: .5, py: .6, borderRadius: 2, textAlign: 'center',
                    bgcolor: low ? 'rgba(240,162,2,.08)' : '#f7f9fd',
                    border: `1px solid ${low ? 'rgba(240,162,2,.5)' : C.line}`,
                  }}>
                    <Typography sx={{
                      fontWeight: 900, fontSize: 16, lineHeight: 1.1,
                      color: low ? C.activeDeep : C.ink,
                    }}>{v}</Typography>
                    <Typography sx={{ fontSize: 9.5, color: C.sub, fontWeight: 700 }}>{f.short}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Section>
        )}

        {/* ── Checklist ── */}
        <Box data-panel-block sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1, px: .5 }}>
          <ClipboardList size={16} color={C.ink} />
          <Typography sx={{ fontWeight: 800, color: C.ink, fontSize: 14.5 }}>
            Material checklist
          </Typography>
          <Chip size="small" label={`${checkedCount}/${total}`}
            sx={{
              ml: 'auto', fontWeight: 800, height: 21,
              bgcolor: allChecked ? 'rgba(18,165,148,.15)' : '#eaeff7',
              color: allChecked ? C.doneDeep : C.sub,
            }} />
        </Box>

        {(template?.sections || []).map((sec) => {
          const secDone = sec.items.filter((it) => localChecks[it.key]).length;
          const secFull = secDone === sec.items.length;
          return (
            <Section
              key={sec.section}
              dense
              icon={<span style={{ fontSize: 16 }}>{SECTION_ICON[sec.icon] || '📋'}</span>}
              title={sec.section}
              badge={`${secDone}/${sec.items.length}`}
              badgeTone={secFull ? 'done' : 'info'}
            >
              <Box>
                {sec.items.map((it) => {
                  const on = !!localChecks[it.key];
                  return (
                    <Box
                      key={it.key}
                      onClick={() => toggle(it.key)}
                      role="checkbox"
                      aria-checked={on}
                      aria-disabled={locked}
                      tabIndex={locked ? -1 : 0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(it.key); }
                      }}
                      sx={{
                        display: 'flex', gap: 1.25, alignItems: 'flex-start',
                        px: 1.75, py: 1.1, cursor: locked ? 'default' : 'pointer',
                        borderTop: `1px solid ${C.line}`,
                        bgcolor: on ? 'rgba(18,165,148,.06)' : 'transparent',
                        '&:hover': { bgcolor: locked ? undefined : 'rgba(0,0,0,.02)' },
                        '&:focus-visible': { outline: `2px solid ${C.active}`, outlineOffset: -2 },
                      }}
                    >
                      <Box sx={{
                        mt: .2, width: 21, height: 21, borderRadius: 1.2, flexShrink: 0,
                        display: 'grid', placeItems: 'center',
                        border: on ? 'none' : `2px solid ${C.road}`,
                        bgcolor: on ? C.done : 'transparent',
                        color: '#fff', transition: 'all .15s',
                      }}>
                        {on && <Check size={14} strokeWidth={3.5} />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: 13.5,
                          color: on ? C.sub : C.ink,
                          textDecoration: on ? 'line-through' : 'none',
                        }}>
                          {it.label}
                        </Typography>
                        {it.hint && (
                          <Typography sx={{ fontSize: 12, color: C.sub, lineHeight: 1.35 }}>
                            {it.hint}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Section>
          );
        })}

        {/* ── Data e assinatura ── */}
        <Box data-panel-block sx={{
          mt: 2, bgcolor: '#fff', borderRadius: 3, p: 2, border: `1px solid ${C.line}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <PenLine size={17} color={C.ink} />
            <Typography sx={{ fontWeight: 800, color: C.ink }}>Date and signature</Typography>
          </Box>

          {preview && !isDone ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              You sign this one when it becomes your stop.
            </Alert>
          ) : isDone ? (
            <>
              <SignatureSvg signatureSvgJson={stop.signature_svg} width="100%" height={110} />
              <Alert severity="success" sx={{ borderRadius: 2, mt: 1 }}>
                Signed{stop.signature ? <> by <b>{stop.signature}</b></> : ''}
                {stop.completed_at ? ` · ${shortTime(stop.completed_at)}` : ''}
                {stop.notes ? <><br />Note: {stop.notes}</> : null}
              </Alert>
            </>
          ) : (
            <>
              <SignaturePad value={signatureSvg} onChange={setSignatureSvg} />
              <TextField
                fullWidth size="small" label="Your name (optional)" value={signature}
                onChange={(e) => setSignature(e.target.value)} sx={{ mt: 1.25, mb: 1.25 }}
              />
              <TextField
                fullWidth size="small" multiline minRows={2}
                label="Anything to report (optional)"
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />
              {!allChecked && (
                <Typography sx={{ fontSize: 12.5, color: C.sub, mt: 1 }}>
                  {total - checkedCount} item(s) still unchecked — you can finish anyway if this
                  hotel did not need them.
                </Typography>
              )}
              <Button
                fullWidth variant="contained" disabled={!signatureSvg || busy}
                onClick={() => onComplete(stop.id, signature.trim(), notes.trim(), signatureSvg)}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Flag size={17} />}
                sx={{
                  mt: 1.75, py: 1.2, borderRadius: 2.5, fontWeight: 900, textTransform: 'none',
                  fontSize: 15, background: `linear-gradient(135deg, ${C.done}, ${C.doneDeep})`,
                  '&:hover': { background: `linear-gradient(135deg, ${C.doneDeep}, ${C.doneDeep})` },
                }}
              >
                Finish this hotel
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Preview da rota inteira — o que separar antes de sair
// ═══════════════════════════════════════════════════════════════════════════
const RoutePreview = ({ open, stops, onClose }) => {
  const bodyRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !bodyRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-preview-row]', {
        y: 16, opacity: 0, duration: 0.35, stagger: 0.05, ease: 'power3.out',
      });
    }, bodyRef);
    return () => ctx.revert();
  }, [open]);

  const withNotes = stops.filter((s) => s.material_notes?.length > 0);
  const totalNotes = stops.reduce((n, s) => n + (s.material_notes?.length || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{
        px: 2.5, py: 2, color: '#fff',
        background: `linear-gradient(135deg, #2b3f66, ${C.ink})`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <PackageCheck size={22} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, letterSpacing: '.14em', opacity: .85, fontWeight: 700 }}>
            BEFORE YOU LEAVE
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
            {stops.length} stops · {totalNotes} note{totalNotes === 1 ? '' : 's'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close"><X size={20} /></IconButton>
      </Box>

      <DialogContent ref={bodyRef} sx={{ bgcolor: C.bg, p: 2 }}>
        {withNotes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: C.sub }}>
            <Inbox size={38} />
            <Typography sx={{ mt: 1, fontWeight: 700 }}>No one wrote anything this week.</Typography>
            <Typography sx={{ fontSize: 13.5 }}>Just take the usual material.</Typography>
          </Box>
        ) : stops.map((s, i) => (
          <Box key={s.id} data-preview-row sx={{
            mb: 1.25, bgcolor: '#fff', borderRadius: 3, border: `1px solid ${C.line}`,
            overflow: 'hidden', opacity: s.material_notes?.length ? 1 : 0.6,
          }}>
            <Box sx={{
              px: 1.75, py: 1, display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: s.status === 'DONE' ? 'rgba(18,165,148,.08)' : '#f7f9fd',
              borderBottom: s.material_notes?.length ? `1px solid ${C.line}` : 'none',
            }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                bgcolor: s.status === 'DONE' ? C.done : C.ink, color: '#fff',
                display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 11,
              }}>{i + 1}</Box>
              <Typography sx={{ fontWeight: 800, color: C.ink, fontSize: 14 }} noWrap>
                {s.hotel_name}
              </Typography>
              {s.status === 'DONE' && (
                <Check size={15} color={C.doneDeep} style={{ marginLeft: 'auto' }} />
              )}
            </Box>

            {s.material_notes?.length ? (
              <Box sx={{ px: 1.75, py: 1.25, display: 'flex', flexDirection: 'column', gap: .75 }}>
                {s.material_notes.map((n, k) => (
                  <Box key={`${n.created_at}-${k}`}>
                    <Typography sx={{ whiteSpace: 'pre-wrap', color: C.ink, fontSize: 14 }}>
                      • {n.note}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.sub }}>
                      {shortTime(n.created_at)}{n.author_name ? ` · ${n.author_name}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ px: 1.75, py: .9 }}>
                <Typography sx={{ fontSize: 13, color: C.sub }}>Nothing written.</Typography>
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Visão geral da semana — tudo que escreveram, por hotel
// ═══════════════════════════════════════════════════════════════════════════
const RANGES = [
  { days: 7,  label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
];

// Paginador — usado nos recados e no histórico de contagens.
const Pager = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .5, py: 1 }}>
      <IconButton size="small" disabled={page <= 0} onClick={() => onPage(page - 1)}
        aria-label="Previous page">
        <ChevronLeft size={16} />
      </IconButton>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.sub, minWidth: 54, textAlign: 'center' }}>
        {page + 1} / {pages}
      </Typography>
      <IconButton size="small" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}
        aria-label="Next page">
        <ChevronRight size={16} />
      </IconButton>
    </Box>
  );
};

const NOTES_PER_PAGE = 8;   // hotéis por página na aba de recados
const HIST_PER_PAGE = 10;   // linhas por página no histórico de contagens

const WeekNotesView = () => {
  const [days, setDays] = useState(7);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/material/notes', { params: { days } })
      .then((r) => { if (alive) setNotes(r.data?.notes || []); })
      .catch(() => { if (alive) setNotes([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return notes;
    return notes.filter(
      (n) => n.note.toLowerCase().includes(t) || (n.hotel_name || '').toLowerCase().includes(t),
    );
  }, [notes, term]);

  // Agrupa por hotel, mantendo o hotel com atividade mais recente no topo.
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((n) => {
      if (!m.has(n.hotel_id)) m.set(n.hotel_id, { hotel: n.hotel_name, items: [] });
      m.get(n.hotel_id).items.push(n);
    });
    return Array.from(m.values());
  }, [filtered]);

  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(groups.length / NOTES_PER_PAGE));
  const pageGroups = useMemo(
    () => groups.slice(page * NOTES_PER_PAGE, page * NOTES_PER_PAGE + NOTES_PER_PAGE),
    [groups, page],
  );

  // Trocar o filtro ou a busca reembaralha a lista; ficar na página 4 de um
  // resultado que agora tem 1 página deixaria a tela vazia.
  useEffect(() => { setPage(0); }, [days, term]);

  useLayoutEffect(() => {
    if (loading || !wrapRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-panel-block]', {
        y: 18, opacity: 0, duration: 0.4, stagger: 0.06, ease: 'power3.out',
      });
    }, wrapRef);
    return () => ctx.revert();
  }, [loading, pageGroups.length, page]);

  return (
    <Box ref={wrapRef} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        {RANGES.map((r) => (
          <Chip
            key={r.days}
            label={r.label}
            onClick={() => setDays(r.days)}
            sx={{
              fontWeight: 800,
              bgcolor: days === r.days ? C.ink : '#fff',
              color: days === r.days ? '#fff' : C.sub,
              border: `1.5px solid ${days === r.days ? C.ink : C.line}`,
            }}
          />
        ))}
        <TextField
          size="small" placeholder="Search hotel or text"
          value={term} onChange={(e) => setTerm(e.target.value)}
          InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 6, opacity: .55 }} /> }}
          sx={{ flex: 1, minWidth: 190, bgcolor: '#fff', borderRadius: 1 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : groups.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: C.sub }}>
          <Inbox size={42} />
          <Typography sx={{ mt: 1, fontWeight: 800, color: C.ink }}>Nothing written</Typography>
          <Typography sx={{ fontSize: 14 }}>
            {term ? 'No note matches that search.' : `No material notes in the last ${days} days.`}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography sx={{ color: C.sub, fontSize: 13.5, mb: 1.5 }}>
            {filtered.length} note{filtered.length === 1 ? '' : 's'} from {groups.length} hotel
            {groups.length === 1 ? '' : 's'} in the last {days} days
          </Typography>

          {/* Fechado por padrão: com 30 dias de recados isto vira uma parede de
              texto. Buscando, abre — ela já sabe o que está procurando.
              O `term` na key força o remount para o defaultOpen valer de novo. */}
          {pageGroups.map((g) => (
            <Section
              key={`${g.hotel}|${term ? 'search' : 'all'}`}
              icon={<MapPin size={15} color={C.ink} />}
              title={g.hotel}
              badge={g.items.length}
              defaultOpen={!!term || groups.length === 1}
            >
              {g.items.map((n, i) => (
                <Box key={`${n.id}-${i}`} sx={{
                  px: 1.75, py: 1.25, borderTop: i === 0 ? 'none' : `1px solid ${C.line}`,
                }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap', color: C.ink, fontSize: 14.5 }}>
                    {n.note}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: C.sub, mt: .4 }}>
                    {shortTime(n.created_at)}
                    {n.author_name ? ` · ${n.author_name}` : ''}
                    {n.source === 'legacy' ? ' · imported' : ''}
                  </Typography>
                </Box>
              ))}
            </Section>
          ))}

          <Pager page={page} pages={pages} onPage={setPage} />
        </>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Quantidades — o que as meninas contam no POS
//
// São os 7 campos do HotelInventoryScreen do POS. `charms` existe na tabela
// mas o app não envia, então não entra aqui.
// Atenção ao dado: hotel_daily_supplies guarda UMA linha por hotel, that gets
// UPDATEd a cada save. Logo não há histórico e `entry_date` é a data do último
// save — por isso a idade de cada contagem aparece em destaque.
// ═══════════════════════════════════════════════════════════════════════════
const SUPPLY_FIELDS = [
  { key: 'pre_made_in',   label: 'Pre-made in',  short: 'PM in' },
  { key: 'pre_made_out',  label: 'Pre-made out', short: 'PM out' },
  { key: 'combs_in',      label: 'Combs in',     short: 'Combs in' },
  { key: 'combs_out',     label: 'Combs out',    short: 'Combs out' },
  { key: 'metal_clips',   label: 'Metal clips',  short: 'Metal',   target: 7 },
  { key: 'plastic_clips', label: 'Plastic clips', short: 'Plastic', target: 6 },
  { key: 'scissors',      label: 'Scissors',     short: 'Scissors', target: 2 },
];

const daysSince = (isoDate) => {
  if (!isoDate) return null;
  const then = fromISO(isoDate);
  const now = fromISO(todayISO());
  return Math.round((now - then) / 86400000);
};

const SupplyCard = ({ row }) => {
  // `id` null = nunca teve registro. Não dá para usar entry_date: o backend
  // devolve a data de hoje quando não há contagem nenhuma (COALESCE em
  // supplies.py), então um hotel que nunca contou apareceria como "today".
  const never = row.id == null;
  const age = never ? null : daysSince(row.entry_date);
  const stale = age !== null && age >= 3;
  const short = SUPPLY_FIELDS.filter((f) => f.target && Number(row[f.key]) < f.target);

  // Histórico fica fechado e só é buscado quando ela abre — a contagem
  // principal continua sendo a última, que já veio junto da lista.
  const [openHist, setOpenHist] = useState(false);
  const [hist, setHist] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [histPage, setHistPage] = useState(0);

  const toggleHist = () => {
    const next = !openHist;
    setOpenHist(next);
    if (next && hist === null && !loadingHist) {
      setLoadingHist(true);
      api.get('/material/supply-history', { params: { hotel_id: row.hotel_id, days: 30 } })
        .then((r) => setHist(r.data?.entries || []))
        .catch(() => setHist([]))
        .finally(() => setLoadingHist(false));
    }
  };

  const histPages = Math.max(1, Math.ceil((hist?.length || 0) / HIST_PER_PAGE));
  const histStart = histPage * HIST_PER_PAGE;

  return (
    <Box data-supply-row sx={{
      mb: 1.25, bgcolor: '#fff', borderRadius: 3, border: `1px solid ${C.line}`, overflow: 'hidden',
      borderLeft: `4px solid ${never ? C.locked : short.length ? C.active : C.done}`,
    }}>
      <Box sx={{
        px: 1.75, py: 1.15, display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: '#f7f9fd', borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap',
      }}>
        <MapPin size={16} color={C.ink} />
        <Typography sx={{ fontWeight: 800, color: C.ink }}>{row.hotel_name}</Typography>

        <Box sx={{ ml: 'auto', display: 'flex', gap: .75, alignItems: 'center' }}>
          {never ? (
            <Chip size="small" label="never counted"
              sx={{ height: 21, fontWeight: 700, bgcolor: '#eef2f8', color: C.sub }} />
          ) : (
            <>
              {short.length > 0 && (
                <Chip size="small" label={`${short.length} below target`}
                  sx={{ height: 21, fontWeight: 800, bgcolor: 'rgba(240,162,2,.16)', color: C.activeDeep }} />
              )}
              <Chip
                size="small"
                label={age === 0 ? 'today' : age === 1 ? 'yesterday' : `${age} days ago`}
                sx={{
                  height: 21, fontWeight: 700,
                  bgcolor: stale ? 'rgba(224,95,138,.14)' : '#eef4ff',
                  color: stale ? '#a3355c' : '#3557b7',
                }}
              />
            </>
          )}
        </Box>
      </Box>

      <Box sx={{
        p: 1.25, display: 'grid', gap: .75,
        gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))',
      }}>
        {SUPPLY_FIELDS.map((f) => {
          const v = Number(row[f.key]) || 0;
          const low = f.target && v < f.target;
          return (
            <Box key={f.key} sx={{
              px: 1, py: .85, borderRadius: 2, textAlign: 'center',
              bgcolor: low ? 'rgba(240,162,2,.10)' : '#f7f9fd',
              border: `1px solid ${low ? 'rgba(240,162,2,.45)' : C.line}`,
            }}>
              <Typography sx={{
                fontWeight: 900, fontSize: 19, lineHeight: 1.1,
                color: low ? C.activeDeep : C.ink,
              }}>
                {v}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: C.sub, fontWeight: 700, letterSpacing: '.02em' }}>
                {f.short}
              </Typography>
              {f.target && (
                <Typography sx={{ fontSize: 10, color: low ? C.activeDeep : C.locked, fontWeight: 700 }}>
                  need {f.target}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Histórico das contagens anteriores ── */}
      <Box
        onClick={toggleHist}
        role="button"
        tabIndex={0}
        aria-expanded={openHist}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleHist(); } }}
        sx={{
          display: 'flex', alignItems: 'center', gap: .75, px: 1.75, py: .9, cursor: 'pointer',
          borderTop: `1px solid ${C.line}`, color: C.sub,
          '&:hover': { bgcolor: '#f7f9fd' },
          '&:focus-visible': { outline: `2px solid ${C.active}`, outlineOffset: -2 },
        }}
      >
        <History size={14} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          {openHist ? 'Hide previous counts' : 'Previous counts'}
        </Typography>
        {loadingHist && <CircularProgress size={12} sx={{ ml: .5 }} />}
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          {openHist ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </Box>
      </Box>

      <Collapse in={openHist} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${C.line}`, bgcolor: '#fbfcfe' }}>
          {hist === null ? (
            <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>
          ) : hist.length <= 1 ? (
            <Typography sx={{ px: 1.75, py: 1.5, fontSize: 13, color: C.sub }}>
              No earlier count in the last 30 days — this is the first one recorded.
            </Typography>
          ) : (
            // tabela larga de propósito: rola aqui dentro, nunca a página
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Box component="table" sx={{
                width: '100%', borderCollapse: 'collapse', fontSize: 12.5,
                '& th, & td': { padding: '7px 10px', whiteSpace: 'nowrap', textAlign: 'right' },
                '& th': {
                  fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase',
                  color: C.sub, fontWeight: 800, borderBottom: `1px solid ${C.line}`,
                },
                '& td': { borderBottom: `1px solid ${C.line}`, fontVariantNumeric: 'tabular-nums' },
                '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
              }}>
                <thead>
                  <tr>
                    <th>When</th>
                    {SUPPLY_FIELDS.map((f) => <th key={f.key}>{f.short}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {hist.slice(histStart, histStart + HIST_PER_PAGE).map((h, k) => {
                    // índice absoluto: a comparação tem que ser com a contagem
                    // seguinte de verdade, não com a última linha da página
                    const i = histStart + k;
                    const prev = hist[i + 1]; // a lista vem do mais novo para o mais velho
                    return (
                      <tr key={h.id}>
                        <td style={{ color: i === 0 ? C.ink : C.sub, fontWeight: i === 0 ? 800 : 500 }}>
                          {shortTime(h.created_at)}
                          {i === 0 ? ' · latest' : ''}
                          {h.source === 'legacy' ? ' · imported' : ''}
                        </td>
                        {SUPPLY_FIELDS.map((f) => {
                          const v = h[f.key];
                          const changed = prev && prev[f.key] !== v;
                          return (
                            <td key={f.key} style={{
                              fontWeight: changed ? 800 : 500,
                              color: changed ? C.activeDeep : C.ink,
                            }}>
                              {v}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
              <Pager page={histPage} pages={histPages} onPage={setHistPage} />
              <Typography sx={{ px: 1.75, pb: 1, fontSize: 11.5, color: C.sub }}>
                {hist.length} count{hist.length === 1 ? '' : 's'} in the last 30 days.
                Highlighted numbers changed from the count below them.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const SupplyCountsView = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [onlyAttention, setOnlyAttention] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/hotel_supplies')
      .then((r) => { if (alive) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Hotel que nunca contou não é alerta — simplesmente não usa isto. Sem esse
  // corte ele apareceria como "7 abaixo da meta" para sempre, só por ter zeros.
  const needsAttention = (r) => {
    if (r.id == null) return false;
    const short = SUPPLY_FIELDS.some((f) => f.target && Number(r[f.key]) < f.target);
    const age = daysSince(r.entry_date);
    return short || (age !== null && age >= 3);
  };

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase();
    let out = rows;
    if (t) out = out.filter((r) => (r.hotel_name || '').toLowerCase().includes(t));
    if (onlyAttention) out = out.filter(needsAttention);
    // Quem contou por último aparece primeiro; quem nunca contou vai pro fim.
    return [...out].sort((a, b) => {
      const na = a.id == null;
      const nb = b.id == null;
      if (na !== nb) return na ? 1 : -1;
      return String(b.entry_date || '').localeCompare(String(a.entry_date || ''));
    });
  }, [rows, term, onlyAttention]);

  useLayoutEffect(() => {
    if (loading || !wrapRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-supply-row]', {
        y: 16, opacity: 0, duration: 0.38, stagger: 0.05, ease: 'power3.out',
      });
    }, wrapRef);
    return () => ctx.revert();
  }, [loading, visible.length]);

  const attentionCount = rows.filter(needsAttention).length;

  return (
    <Box ref={wrapRef} sx={{ mt: 2 }}>
      <Alert severity="info" icon={<PackageCheck size={18} />} sx={{ borderRadius: 3, mb: 2 }}>
        The counts the girls type in the POS. Each hotel keeps only its <b>latest</b> count, so the
        chip on the right tells you how old it is.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip
          label={onlyAttention ? `Needs attention (${attentionCount})` : `Show only what needs attention (${attentionCount})`}
          onClick={() => setOnlyAttention((v) => !v)}
          sx={{
            fontWeight: 800,
            bgcolor: onlyAttention ? C.ink : '#fff',
            color: onlyAttention ? '#fff' : C.sub,
            border: `1.5px solid ${onlyAttention ? C.ink : C.line}`,
          }}
        />
        <TextField
          size="small" placeholder="Search hotel"
          value={term} onChange={(e) => setTerm(e.target.value)}
          InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 6, opacity: .55 }} /> }}
          sx={{ flex: 1, minWidth: 180, bgcolor: '#fff', borderRadius: 1 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: C.sub }}>
          <Inbox size={42} />
          <Typography sx={{ mt: 1, fontWeight: 800, color: C.ink }}>Nothing to show</Typography>
        </Box>
      ) : (
        visible.map((r) => <SupplyCard key={r.hotel_id} row={r} />)
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Mapa dos hotéis — Leaflet + tiles do OpenStreetMap
//
// Sem chave e sem cadastro: o Leaflet é MIT e o tile server do OSM é aberto
// para volume baixo como este. Os pinos são divIcon (HTML) e não imagem, por
// dois motivos: o marker padrão do Leaflet aponta para um PNG que o build do
// CRA não resolve, e em HTML dá para escrever o número da parada dentro dele.
// ═══════════════════════════════════════════════════════════════════════════
const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const FLORIDA_CENTER = [28.42, -81.47];  // só o enquadramento inicial, até os pins mandarem

// Os popups são HTML montado na mão; nome de hotel vem do banco.
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const pinIcon = (label, bg) => L.divIcon({
  className: '',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
  html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);background:${bg};border:2.5px solid #fff;
    box-shadow:0 2px 7px rgba(0,0,0,.35);display:flex;align-items:center;
    justify-content:center"><span style="transform:rotate(45deg);color:#fff;
    font:900 12px/1 system-ui,sans-serif">${esc(label)}</span></div>`,
});

const HotelMap = ({ pins, stops, myPos }) => {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !hostRef.current) return undefined;
    const map = L.map(hostRef.current, { scrollWheelZoom: false }).setView(FLORIDA_CENTER, 9);
    L.tileLayer(OSM_TILES, { attribution: OSM_ATTR, maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // O container acabou de nascer; sem isto o Leaflet desenha só metade dos tiles.
    const t = setTimeout(() => map.invalidateSize(), 0);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    // Só os hotéis do roteiro do dia. O mapa é para ela escolher o caminho, e
    // pino de hotel que ela não vai visitar hoje só atrapalha a leitura.
    const routeByHotel = new Map(
      stops.map((s, i) => [s.hotel_id, { order: i + 1, done: s.status === 'DONE' }]),
    );
    const bounds = [];

    pins.filter((p) => p.lat != null && p.lng != null && routeByHotel.has(p.id)).forEach((p) => {
      const r = routeByHotel.get(p.id);
      const bg = r.done ? C.done : C.active;
      const dist = distanceM(myPos, { lat: p.lat, lng: p.lng });
      L.marker([p.lat, p.lng], { icon: pinIcon(r.order, bg) })
        .addTo(layer)
        .bindPopup(
          `<div style="font:800 14px/1.3 system-ui,sans-serif;color:${C.ink}">${esc(p.name)}</div>`
          + `<div style="font:800 12px system-ui,sans-serif;color:${bg};margin-top:2px">`
          + `Stop ${r.order}${r.done ? ' · done' : ''}</div>`
          + (dist != null ? `<div style="font:500 12px system-ui,sans-serif;color:${C.sub}">`
                            + `${prettyDistance(dist)} from you</div>` : '')
          + (p.address ? `<a href="${esc(mapsHref(p.address))}" rel="noopener"
               style="font:800 12px system-ui,sans-serif;color:#3557b7;display:inline-block;
               margin-top:6px">Open in maps</a>` : ''),
        );
      bounds.push([p.lat, p.lng]);
    });

    if (myPos) {
      L.circleMarker([myPos.lat, myPos.lng], {
        radius: 8, color: '#fff', weight: 3, fillColor: '#3557b7', fillOpacity: 1,
      }).addTo(layer).bindPopup('You are here');
      bounds.push([myPos.lat, myPos.lng]);
    }

    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [pins, stops, myPos]);

  return (
    <Box ref={hostRef} sx={{
      height: { xs: 400, sm: 460 }, borderRadius: 3, overflow: 'hidden',
      border: `1px solid ${C.line}`, bgcolor: '#e9eef6',
      '& .leaflet-container': { height: '100%', width: '100%', fontFamily: 'inherit' },
    }} />
  );
};

const LEGEND = [
  { color: C.active, text: 'Still to do' },
  { color: C.done,   text: 'Already done' },
];

// Corrige um resort de cada vez: o lote erra em endereço mal escrito ou acha a
// cidade em vez do resort. Mostra o que o Nominatim entendeu, que é a única
// forma de perceber que o pino caiu no lugar errado sem ir olhar no mapa.
const FixResort = ({ pins, onReload, notify }) => {
  const [hotelId, setHotelId] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (id) => {
    setHotelId(id);
    setResult(null);
    setAddress(pins.find((p) => String(p.id) === String(id))?.address || '');
  };

  const search = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.put(`/material/hotels/${hotelId}/geocode`, { address });
      setResult(data);
      await onReload();
    } catch (e) {
      notify(e?.response?.data?.msg || 'Could not search that address', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{
      mt: 2, p: 1.75, bgcolor: '#fff', borderRadius: 3, border: `1px solid ${C.line}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <MapPinned size={16} color={C.ink} />
        <Typography sx={{ fontWeight: 800, color: C.ink, fontSize: 14.5 }}>
          Wrong pin? Fix one resort
        </Typography>
      </Box>

      <TextField
        select fullWidth size="small" label="Resort" value={hotelId}
        onChange={(e) => pick(e.target.value)} sx={{ mb: 1.25 }}
      >
        {pins.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.lat != null ? '✓' : '⚠'} {p.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth size="small" multiline minRows={2} label="Address to search"
        placeholder="Street, number, city, state — the more complete, the better"
        value={address} onChange={(e) => setAddress(e.target.value)}
        disabled={!hotelId} sx={{ mb: 1.25 }}
      />

      <Button
        variant="contained" disabled={!hotelId || !address.trim() || busy} onClick={search}
        startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <Search size={16} />}
        sx={{
          textTransform: 'none', fontWeight: 800, borderRadius: 2.5, px: 2.5,
          background: `linear-gradient(135deg, #2b3f66, ${C.ink})`,
        }}
      >
        Search again
      </Button>

      {result && (
        <Alert severity={result.found ? 'success' : 'warning'} sx={{ borderRadius: 2.5, mt: 1.5 }}>
          {result.found ? (
            <>
              <b>{result.name}</b> is on the map now. It matched:
              <Typography sx={{ fontSize: 12.5, mt: .4, color: C.sub }}>
                {result.matched}
              </Typography>
              <Typography sx={{ fontSize: 12.5, mt: .4 }}>
                If that is not the right place, edit the address and search again.
              </Typography>
            </>
          ) : (
            <>Still could not find <b>{result.name}</b>. Try the plain street address with city
              and state, without the resort name.</>
          )}
        </Alert>
      )}

      <Typography sx={{ fontSize: 11.5, color: C.sub, mt: 1.25 }}>
        The address you type here is saved on the resort — it is the same one the Navigate button uses.
      </Typography>
    </Box>
  );
};

const MapView = ({ pins, info, stops, date, isManagement, loc, onReload, notify }) => {
  const [geocoding, setGeocoding] = useState(false);
  // Quais endereços o Nominatim não achou. Só o número não serve para nada —
  // ela precisa saber qual hotel arrumar.
  const [failed, setFailed] = useState([]);

  const onRouteWithPin = useMemo(() => {
    const ids = new Set(stops.map((s) => s.hotel_id));
    return pins.filter((p) => p.lat != null && ids.has(p.id));
  }, [pins, stops]);

  const geocode = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post('/material/geocode-hotels');
      const { resolved = 0, failed: notFound = [], remaining = 0 } = data || {};
      setFailed(notFound);
      await onReload();
      notify(
        `${resolved} resort${resolved === 1 ? '' : 's'} placed on the map`
        + (notFound.length ? ` · ${notFound.length} address not found` : '')
        + (remaining ? ` · ${remaining} still missing, run it again` : ''),
        notFound.length || remaining ? 'info' : 'success',
      );
    } catch (e) {
      notify(e?.response?.data?.msg || 'Could not fetch the coordinates', 'error');
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <LocationBanner status={loc.status} accuracy={loc.accuracy} onLocate={loc.locate} />

      {info.missing > 0 && (
        <Alert
          severity="warning" sx={{ borderRadius: 3, mb: 1.5 }}
          action={isManagement ? (
            <Button
              size="small" onClick={geocode} disabled={geocoding}
              startIcon={geocoding ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}
            >
              {geocoding ? 'Looking…' : 'Place them'}
            </Button>
          ) : null}
        >
          {info.missing} resort{info.missing === 1 ? '' : 's'} not on the map yet
          {isManagement
            ? ' — this reads the address and finds it. Takes about a second each.'
            : ' — ask a manager to open this tab and place them.'}
        </Alert>
      )}

      {failed.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 3, mb: 1.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, mb: .5 }}>
            {failed.length} address{failed.length === 1 ? '' : 'es'} could not be found
          </Typography>
          {failed.map((f) => (
            <Typography key={f.name} sx={{ fontSize: 12.5, lineHeight: 1.5 }}>
              • <b>{f.name}</b> — {f.address}
            </Typography>
          ))}
          <Typography sx={{ fontSize: 12.5, mt: .75 }}>
            Fix them one by one below. Deactivated resorts and the ones with no address are
            never tried, so these are all active resorts with an address written.
          </Typography>
        </Alert>
      )}

      {info.no_address > 0 && (
        <Alert severity="info" sx={{ borderRadius: 3, mb: 1.5 }}>
          {info.no_address} resort{info.no_address === 1 ? ' has' : 's have'} no address saved —
          fill it in Setup &gt; Resorts and it shows up here.
        </Alert>
      )}

      {stops.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7, color: C.sub }}>
          <MapIcon size={42} />
          <Typography sx={{ mt: 1, fontWeight: 800, color: C.ink }}>No route for this day</Typography>
          <Typography sx={{ fontSize: 14 }}>
            The map shows the stops of {prettyDate(date)} — pick a day that has a route.
          </Typography>
        </Box>
      ) : onRouteWithPin.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7, color: C.sub }}>
          <MapIcon size={42} />
          <Typography sx={{ mt: 1, fontWeight: 800, color: C.ink }}>Nothing to show yet</Typography>
          <Typography sx={{ fontSize: 14 }}>
            None of today’s resorts has a location saved.
          </Typography>
        </Box>
      ) : (
        <>
          <HotelMap pins={pins} stops={stops} myPos={loc.position} />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.25, px: .5 }}>
            {LEGEND.map((l) => (
              <Box key={l.text} sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
                <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: l.color }} />
                <Typography sx={{ fontSize: 12, color: C.sub, fontWeight: 700 }}>{l.text}</Typography>
              </Box>
            ))}
            {loc.position && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
                <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#3557b7' }} />
                <Typography sx={{ fontSize: 12, color: C.sub, fontWeight: 700 }}>You</Typography>
              </Box>
            )}
          </Box>

          <Typography sx={{ fontSize: 12, color: C.sub, mt: 1, px: .5 }}>
            Pins are the stops of this route, numbered in order. Tap one for the distance and to
            open it in maps.
          </Typography>
        </>
      )}

      {isManagement && <FixResort pins={pins} onReload={onReload} notify={notify} />}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Construtor de roteiro — gerência
// ═══════════════════════════════════════════════════════════════════════════
const RouteBuilder = ({ hotels, initialStops, date, onSave, saving, onCancel }) => {
  const [picked, setPicked] = useState(() => initialStops.map((s) => s.hotel_id));
  const listRef = useRef(null);

  // Uma parada já concluída não pode sair do roteiro — perderia o registro.
  const lockedIds = useMemo(
    () => new Set(initialStops.filter((s) => s.status === 'DONE').map((s) => s.hotel_id)),
    [initialStops],
  );

  const byId = useMemo(() => {
    const m = {};
    hotels.forEach((h) => { m[h.id] = h; });
    return m;
  }, [hotels]);

  const add = (id) => setPicked((p) => (p.includes(id) ? p : [...p, id]));
  const remove = (id) => { if (!lockedIds.has(id)) setPicked((p) => p.filter((x) => x !== id)); };
  const move = (i, dir) => setPicked((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const n = [...p];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  useLayoutEffect(() => {
    if (!listRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('[data-picked]', { x: -14, opacity: 0, duration: 0.3, stagger: 0.04, ease: 'power2.out' });
    }, listRef);
    return () => ctx.revert();
  }, [picked.length]);

  const available = hotels.filter((h) => !picked.includes(h.id));

  return (
    <Box sx={{ mt: 2 }}>
      <Alert severity="info" icon={<Car size={18} />} sx={{ borderRadius: 3, mb: 2 }}>
        Click the hotels <b>in the order she will drive</b>. The first one you pick is the first stop.
      </Alert>

      <Box ref={listRef} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 800, color: C.ink, mb: 1 }}>
          Route for {prettyDate(date)} · {picked.length} stop{picked.length === 1 ? '' : 's'}
        </Typography>

        {picked.length === 0 && (
          <Box sx={{
            border: `2px dashed ${C.road}`, borderRadius: 3, p: 3, textAlign: 'center', color: C.sub,
          }}>
            No hotels yet — pick them below.
          </Box>
        )}

        {picked.map((id, i) => (
          <Box key={id} data-picked sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: '#fff',
            border: `1.5px solid ${C.line}`, borderRadius: 2.5, px: 1.5, py: 1, mb: .75,
          }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(145deg, ${C.active}, ${C.activeDeep})`,
              color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 13,
            }}>{i + 1}</Box>
            <Typography sx={{ fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }} noWrap>
              {byId[id]?.name || `Hotel ${id}`}
            </Typography>
            {lockedIds.has(id) && (
              <Chip size="small" label="done" sx={{
                height: 20, fontWeight: 700, bgcolor: 'rgba(18,165,148,.14)', color: C.doneDeep,
              }} />
            )}
            <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">
              <ChevronLeft size={16} style={{ transform: 'rotate(90deg)' }} />
            </IconButton>
            <IconButton size="small" onClick={() => move(i, 1)}
              disabled={i === picked.length - 1} aria-label="Move down">
              <ChevronRight size={16} style={{ transform: 'rotate(90deg)' }} />
            </IconButton>
            <Tooltip title={lockedIds.has(id) ? 'Already done — cannot remove' : 'Remove'}>
              <span>
                <IconButton size="small" onClick={() => remove(id)}
                  disabled={lockedIds.has(id)} aria-label="Remove">
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontWeight: 800, color: C.ink, mb: 1 }}>Add a hotel</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {available.map((h) => (
          <Chip key={h.id} label={h.name} onClick={() => add(h.id)} icon={<Plus size={15} />}
            sx={{
              fontWeight: 700, bgcolor: '#fff', border: `1.5px solid ${C.line}`,
              '&:hover': { borderColor: C.active, bgcolor: 'rgba(240,162,2,.06)' },
            }} />
        ))}
        {available.length === 0 && (
          <Typography sx={{ color: C.sub, fontSize: 14 }}>
            All hotels are already in the route.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained" disabled={picked.length === 0 || saving}
          onClick={() => onSave(picked)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <RouteIcon size={17} />}
          sx={{
            fontWeight: 900, textTransform: 'none', borderRadius: 2.5, px: 3,
            background: `linear-gradient(135deg, #2b3f66, ${C.ink})`,
          }}
        >
          Save route
        </Button>
        {onCancel && (
          <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 700, color: C.sub }}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Página
// ═══════════════════════════════════════════════════════════════════════════
export default function MaterialPage() {
  const { user } = useAuth();
  const roleId = Number(user?.role_id);
  const isManagement = roleId === 2 || roleId === 3;
  // Material (5) monta a própria rota; o resto do painel segue management-only.
  const canEditRoute = isManagement || roleId === 5;

  const [date, setDate] = useState(todayISO());
  const [template, setTemplate] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [supplyByHotel, setSupplyByHotel] = useState({});
  const [pins, setPins] = useState([]);
  const [pinInfo, setPinInfo] = useState({ missing: 0, no_address: 0 });
  const loc = useMyLocation();
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyStop, setBusyStop] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openStopId, setOpenStopId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [view, setView] = useState('route'); // 'route' | 'notes'
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const headerRef = useRef(null);
  const celebrateRef = useRef(null);

  const notify = useCallback(
    (msg, severity = 'success') => setSnack({ open: true, msg, severity }),
    [],
  );

  // Os pins também alimentam a distância da barra de navegação, então são
  // carregados com a página e não só quando ela abre a aba do mapa.
  const loadPins = useCallback(async () => {
    try {
      const { data } = await api.get('/material/hotel-pins');
      setPins(data?.pins || []);
      setPinInfo({ missing: data?.missing || 0, no_address: data?.no_address || 0 });
    } catch {
      setPins([]);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/material/checklist-template'),
      api.get('/hotels'),
      api.get('/hotel_supplies'),
    ]).then(([t, h, s]) => {
      if (!alive) return;
      setTemplate(t.data);
      const list = Array.isArray(h.data) ? h.data : (h.data?.hotels || []);
      setHotels(list.filter((x) => (x.status || 'active') === 'active'));
      const m = {};
      (Array.isArray(s.data) ? s.data : []).forEach((r) => { m[r.hotel_id] = r; });
      setSupplyByHotel(m);
    }).catch(() => { if (alive) notify('Could not load the checklist', 'error'); });
    loadPins();
    return () => { alive = false; };
  }, [notify, loadPins]);

  const loadRoute = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/material/routes/${d}/detail`);
      setStops(res.data?.stops || []);
      setEditing(false);
    } catch {
      setStops([]);
      notify('Could not load the route', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadRoute(date); }, [date, loadRoute]);

  useLayoutEffect(() => {
    if (!headerRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, { y: -16, opacity: 0, duration: 0.5, ease: 'power3.out' });
    }, headerRef);
    return () => ctx.revert();
  }, []);

  const saveRoute = async (hotelIds) => {
    setSaving(true);
    try {
      await api.post('/material/routes', { route_date: date, hotel_ids: hotelIds });
      notify('Route saved');
      await loadRoute(date);
    } catch (e) {
      notify(e?.response?.data?.msg || 'Could not save the route', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleCheck = async (stopId, itemKey, checked) => {
    // Otimista: ela está de pé no quiosque, não pode esperar a rede.
    setStops((prev) => prev.map((s) => (s.id !== stopId ? s : {
      ...s,
      checks: { ...s.checks, [itemKey]: checked },
      checked_count: Math.max(0, (s.checked_count || 0) + (checked ? 1 : -1)),
      status: s.status === 'PENDING' ? 'IN_PROGRESS' : s.status,
    })));
    try {
      await api.put(`/material/stops/${stopId}/checks`, { item_key: itemKey, checked });
    } catch {
      notify('Could not save that item — check your connection', 'error');
      loadRoute(date);
    }
  };

  const completeStop = async (stopId, signature, notes, signatureSvg) => {
    setBusyStop(true);
    try {
      const res = await api.post(`/material/stops/${stopId}/complete`, {
        signature, notes, signature_svg: signatureSvg,
      });
      setOpenStopId(null);
      await loadRoute(date);
      if (res.data?.route_complete) {
        celebrate();
        notify('Route finished — every hotel is done!');
      } else {
        notify(`Hotel done · ${res.data?.done_stops}/${res.data?.total_stops}`);
      }
    } catch (e) {
      notify(e?.response?.data?.msg || 'Could not finish this hotel', 'error');
    } finally {
      setBusyStop(false);
    }
  };

  // Confete quando a rota inteira fecha — GSAP movendo divs, sem lib extra.
  const celebrate = () => {
    const host = celebrateRef.current;
    if (!host) return;
    const colors = [C.done, C.active, '#3557b7', '#e05f8a', '#7c5cd6'];
    for (let i = 0; i < 70; i += 1) {
      const bit = document.createElement('div');
      bit.style.cssText = `position:absolute;top:40%;left:50%;width:9px;height:14px;`
        + `border-radius:2px;background:${colors[i % colors.length]};pointer-events:none;`;
      host.appendChild(bit);
      gsap.to(bit, {
        x: gsap.utils.random(-320, 320),
        y: gsap.utils.random(-360, 260),
        rotation: gsap.utils.random(-540, 540),
        opacity: 0,
        duration: gsap.utils.random(1.1, 2),
        ease: 'power2.out',
        onComplete: () => bit.remove(),
      });
    }
  };

  const shiftDay = (delta) => {
    const d = fromISO(date);
    d.setDate(d.getDate() + delta);
    setDate(toISO(d));
  };

  const doneCount = stops.filter((s) => s.status === 'DONE').length;
  const pct = stops.length ? Math.round((doneCount / stops.length) * 100) : 0;
  const allDone = stops.length > 0 && doneCount === stops.length;
  const nextStop = stops.find((s) => s.status !== 'DONE');
  const openStop = openStopId ? stops.find((s) => s.id === openStopId) || null : null;
  // Só a parada da vez pode ser marcada; o resto abre como leitura.
  const openIsPreview = !!openStop && openStop.status !== 'DONE' && openStop.id !== nextStop?.id;

  const pinById = useMemo(() => {
    const m = {};
    pins.forEach((p) => { m[p.id] = p; });
    return m;
  }, [pins]);

  // Distância até a próxima parada — null se ela não liberou a localização ou
  // se o hotel ainda não tem coordenada. Nos dois casos a barra volta a ser a
  // de sempre, sem travar nada.
  const nextDistance = useMemo(() => {
    const p = nextStop ? pinById[nextStop.hotel_id] : null;
    return p ? distanceM(loc.position, { lat: p.lat, lng: p.lng }) : null;
  }, [nextStop, pinById, loc.position]);

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 3 }, maxWidth: 820, mx: 'auto', position: 'relative',
      // Rede de segurança contra scroll lateral. `clip` e não `hidden` de
      // propósito: hidden criaria um contexto de rolagem e mataria o
      // position:sticky da barra de navegação.
      overflowX: 'clip',
    }}>
      {/* Confete: as partículas voam até 320px do centro e vazavam pela
          lateral, empurrando a página. */}
      <Box ref={celebrateRef} sx={{
        position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2000,
      }} />

      {/* ── Cabeçalho ── */}
      <Box ref={headerRef} sx={{
        borderRadius: 4, p: 2.5, color: '#fff',
        background: allDone
          ? `linear-gradient(135deg, ${C.done}, ${C.doneDeep})`
          : `linear-gradient(135deg, #2b3f66, ${C.ink})`,
        boxShadow: '0 8px 26px rgba(18,32,58,.24)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RouteIcon size={26} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, letterSpacing: '.16em', opacity: .8, fontWeight: 700 }}>
              MATERIAL ROUTE
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
              {prettyDate(date)}
            </Typography>
          </Box>
          <IconButton onClick={() => shiftDay(-1)} sx={{ color: '#fff' }} aria-label="Previous day">
            <ChevronLeft size={20} />
          </IconButton>
          <Button size="small" onClick={() => setDate(todayISO())}
            sx={{ color: '#fff', textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
            Today
          </Button>
          <IconButton onClick={() => shiftDay(1)} sx={{ color: '#fff' }} aria-label="Next day">
            <ChevronRight size={20} />
          </IconButton>
        </Box>

        {stops.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .75 }}>
              <Typography sx={{ fontWeight: 800 }}>
                {doneCount} of {stops.length} hotels done
              </Typography>
              <Typography sx={{ fontWeight: 900 }}>{pct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct} sx={{
              height: 9, borderRadius: 5, bgcolor: 'rgba(255,255,255,.25)',
              '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: allDone ? '#fff' : C.active },
            }} />
            {nextStop && (
              <Typography sx={{ mt: 1, fontSize: 13.5, opacity: .92 }}>
                Next stop: <b>{nextStop.hotel_name}</b>
              </Typography>
            )}
            {allDone && (
              <Typography sx={{ mt: 1, fontWeight: 800 }}>🎉 Route complete — great job!</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* ── Switch Route | Week notes | Counts ── */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Box sx={{
          display: 'inline-flex', p: .5, borderRadius: 999, maxWidth: '100%',
          bgcolor: '#e9eef6', border: `1px solid ${C.line}`,
        }}>
          {[
            { key: 'route', label: 'Route', icon: <RouteIcon size={14} /> },
            { key: 'map',   label: 'Map',   icon: <MapIcon size={14} /> },
            { key: 'notes', label: 'Notes', icon: <MessageSquareText size={14} /> },
            { key: 'stock', label: 'Counts', icon: <PackageCheck size={14} /> },
          ].map((t) => {
            const on = view === t.key;
            return (
              <Box
                key={t.key}
                onClick={() => setView(t.key)}
                role="tab"
                aria-selected={on}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setView(t.key); } }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .6,
                  px: { xs: 1.5, sm: 2.25 }, py: .85, borderRadius: 999, cursor: 'pointer',
                  fontWeight: 800, fontSize: { xs: 12.5, sm: 13.5 }, whiteSpace: 'nowrap',
                  bgcolor: on ? '#fff' : 'transparent',
                  color: on ? C.ink : C.sub,
                  boxShadow: on ? '0 2px 6px rgba(18,32,58,.14)' : 'none',
                  transition: 'background-color .18s, color .18s',
                  '&:focus-visible': { outline: `2px solid ${C.active}`, outlineOffset: 2 },
                }}
              >
                {t.icon}
                {t.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {view === 'notes' ? (
        <WeekNotesView />
      ) : view === 'stock' ? (
        <SupplyCountsView />
      ) : view === 'map' ? (
        <MapView
          pins={pins}
          info={pinInfo}
          stops={stops}
          date={date}
          isManagement={isManagement}
          loc={loc}
          onReload={loadPins}
          notify={notify}
        />
      ) : loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
      ) : editing || (canEditRoute && stops.length === 0) ? (
        <RouteBuilder
          hotels={hotels}
          initialStops={stops}
          date={date}
          onSave={saveRoute}
          saving={saving}
          onCancel={stops.length ? () => setEditing(false) : null}
        />
      ) : stops.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7, color: C.sub }}>
          <Box sx={{ fontSize: 52, mb: 1 }}>🗺️</Box>
          <Typography sx={{ fontWeight: 800, color: C.ink, mb: .5 }}>No route for this day</Typography>
          <Typography sx={{ fontSize: 14 }}>Management still has to set it up.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              size="small" onClick={() => setPreviewOpen(true)} startIcon={<Eye size={15} />}
              sx={{
                textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 1.75,
                color: C.ink, border: `1.5px solid ${C.line}`, bgcolor: '#fff',
              }}
            >
              Preview the whole route
            </Button>
            {canEditRoute && (
              <Button size="small" onClick={() => setEditing(true)} startIcon={<PenLine size={15} />}
                sx={{ textTransform: 'none', fontWeight: 700, color: C.sub }}>
                Edit route
              </Button>
            )}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <LocationBanner status={loc.status} accuracy={loc.accuracy} onLocate={loc.locate} compact />
          </Box>
          <Trail stops={stops} onOpen={(s) => setOpenStopId(s.id)} />
          <NavigateBar
            stop={nextStop}
            total={stops.length}
            doneCount={doneCount}
            distance={nextDistance}
            onOpen={(s) => setOpenStopId(s.id)}
          />
        </>
      )}

      <StopPanel
        open={!!openStop}
        stop={openStop}
        // Hotel com a tag Sand Art usa o outro checklist; os dois vêm juntos
        // no mesmo GET, então trocar é só escolher aqui.
        template={openStop?.is_sandart ? template?.sandart : template}
        preview={openIsPreview}
        supply={openStop ? supplyByHotel[openStop.hotel_id] : null}
        onClose={() => setOpenStopId(null)}
        onToggle={toggleCheck}
        onComplete={completeStop}
        busy={busyStop}
      />

      <RoutePreview
        open={previewOpen}
        stops={stops}
        onClose={() => setPreviewOpen(false)}
      />

      <Snackbar
        open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
